export const M1_UNIT_3 = {
  id: "m1-u3-talking-yourself",
  title: "Unit 3: Talking About Yourself",
  topic: "To be + Age & Nationality",
  iconType: "user",
  grammarImage: "/m1-u3-ages-nationalities.jpg",
  grammarText: "Unit focus: subject pronouns (I, you, he, she...) + to be forms (am, is, are) + numbers 1-100 + age (I am 25 years old) + nationalities (I am Mexican).",

  sets: [

    // ===================================================================
    // SET 1: SUBJECT PRONOUNS (A1.0)
    // ===================================================================
    {
      id: "m1-u3-set1",
      title: "Set 1: I, You, He, She",
      context: `*Lunes - Primer día de clase*

Carlos llega a su primera clase de inglés.
La profesora señala: "I, you, he, she..."
Carlos escucha con atención.

*Monday - First day of class*

Carlos arrives at his first English class.
The teacher points: "I, you, he, she..."
Carlos listens carefully.`,
      
      questions: [
        {
          type: "match",
          q: "Match the pronoun to its Spanish meaning:",
          pairs: [
            { left: "I", right: "Yo" },
            { left: "You", right: "Tú" },
            { left: "He", right: "Él" }
          ],
          a: "completed"
        },
        
        {
          type: "match",
          q: "Match more pronouns:",
          pairs: [
            { left: "She", right: "Ella" },
            { left: "We", right: "Nosotros" },
            { left: "They", right: "Ellos" }
          ],
          a: "completed",
          successTip: "6 subject pronouns! I, You, He, She, We, They = foundation of English! 🎯"
        },
        
        {
          type: "mc",
          q: "___ am Carlos. (talking about yourself)",
          options: ["I", "You", "He"],
          a: "I",
          tip: "I = yo (yourself)."
        },
        
        {
          type: "mc",
          q: "Carlos talks about his teacher: '___ is nice.'",
          options: ["She", "He", "I"],
          a: "She",
          tip: "Teacher (woman) = She."
        },
        
        {
          type: "mc",
          q: "Carlos and his friend: '___ are students.'",
          options: ["We", "They", "You"],
          a: "We",
          tip: "Carlos + friend = We (nosotros)."
        },
        
        {
          type: "match",
          q: "Match singular and plural:",
          pairs: [
            { left: "I (singular)", right: "We (plural)" },
            { left: "He/She (singular)", right: "They (plural)" },
            { left: "You", right: "You (both!)" }
          ],
          a: "completed",
          successTip: "You = tú AND ustedes! Same word for singular and plural! English is weird! 😅"
        },
        
        {
          type: "mc",
          q: "Talking about two friends: '___ are from Mexico.'",
          options: ["They", "We", "She"],
          a: "They",
          tip: "Two friends (not including you) = They."
        },
        
        {
          type: "mc",
          q: "Teacher asks Carlos: 'Where are ___ from?'",
          options: ["you", "he", "they"],
          a: "you",
          tip: "Talking to Carlos = You."
        },
        
        {
          type: "mc",
          q: "Carlos talks about a male friend: '___ is 22.'",
          options: ["He", "She", "They"],
          a: "He",
          tip: "Male friend = He."
        },
        
        {
          type: "mc",
          q: "Carlos and classmates: '___ are beginners.'",
          options: ["We", "They", "You"],
          a: "We",
          successTip: "We vs They! WE = includes you. THEY = doesn't include you. Important! 👥"
        }
      ]
    },

    // ===================================================================
    // SET 2: TO BE - AM/IS/ARE (A1.0)
    // ===================================================================
    {
      id: "m1-u3-set2",
      title: "Set 2: Am, Is, Are",
      context: `*Clase de inglés*

La profesora escribe en el pizarrón:
I am
You are
He is / She is

*English class*

The teacher writes on the board:
I am
You are
He is / She is`,
      
      questions: [
        {
          type: "match",
          q: "Match each pronoun to its verb:",
          pairs: [
            { left: "I", right: "am" },
            { left: "You", right: "are" },
            { left: "He", right: "is" }
          ],
          a: "completed",
          successTip: "I am, You are, He is! These 3 = 90% of English! Learn them! 🔑"
        },
        
        {
          type: "mc",
          q: "I ___ Carlos.",
          options: ["am", "is", "are"],
          a: "am",
          tip: "I always uses 'am'."
        },
        
        {
          type: "mc",
          q: "You ___ a student.",
          options: ["are", "am", "is"],
          a: "are",
          tip: "You always uses 'are'."
        },
        
        {
          type: "mc",
          q: "She ___ from Spain.",
          options: ["is", "am", "are"],
          a: "is",
          tip: "He/She/It uses 'is'."
        },
        
        {
          type: "match",
          q: "Match plural pronouns to their verb:",
          pairs: [
            { left: "We", right: "are" },
            { left: "You (plural)", right: "are" },
            { left: "They", right: "are" }
          ],
          a: "completed",
          successTip: "Plural = always ARE! We are, You are, They are. Easy pattern! 📊"
        },
        
        {
          type: "mc",
          q: "They ___ teachers.",
          options: ["are", "is", "am"],
          a: "are",
          tip: "They (plural) = are."
        },
        
        {
          type: "mc",
          q: "He ___ 25.",
          options: ["is", "am", "are"],
          a: "is",
          tip: "He/She/It = is."
        },
        
        {
          type: "mc",
          q: "We ___ friends.",
          options: ["are", "is", "am"],
          a: "are",
          tip: "We (plural) = are."
        },
        
        {
          type: "mc",
          q: "It ___ a book.",
          options: ["is", "am", "are"],
          a: "is",
          tip: "It (singular) = is."
        },
        
        {
          type: "match",
          q: "Match the pattern:",
          pairs: [
            { left: "I am", right: "Singular" },
            { left: "He/She/It is", right: "Singular" },
            { left: "You/We/They are", right: "Plural" }
          ],
          a: "completed",
          successTip: "Pattern! AM = only 'I'. IS = singular (he/she/it). ARE = plural + 'you'. Master this! 🎓"
        }
      ]
    },

    // ===================================================================
    // SET 3: NUMBERS 1-12 (A1.0)
    // ===================================================================
    {
      id: "m1-u3-set3",
      title: "Set 3: Numbers 1-12",
      context: `*Actividad en clase*

La profesora cuenta: "One, two, three..."
Carlos repite: "One, two, three..."
Todos practican hasta doce.

*Class activity*

The teacher counts: "One, two, three..."
Carlos repeats: "One, two, three..."
Everyone practices up to twelve.`,
      
      questions: [
        {
          type: "match",
          q: "Match the number to its word:",
          pairs: [
            { left: "1", right: "One" },
            { left: "2", right: "Two" },
            { left: "3", right: "Three" }
          ],
          a: "completed"
        },
        
        {
          type: "match",
          q: "Match more numbers:",
          pairs: [
            { left: "4", right: "Four" },
            { left: "5", right: "Five" },
            { left: "6", right: "Six" }
          ],
          a: "completed"
        },
        
        {
          type: "mc",
          q: "What number is this? 7",
          options: ["Seven", "Seventeen", "Seventy"],
          a: "Seven",
          tip: "7 = Seven."
        },
        
        {
          type: "mc",
          q: "What number is this? 8",
          options: ["Eight", "Eighteen", "Eighty"],
          a: "Eight",
          tip: "8 = Eight."
        },
        
        {
          type: "match",
          q: "Match the last numbers:",
          pairs: [
            { left: "9", right: "Nine" },
            { left: "10", right: "Ten" },
            { left: "11", right: "Eleven" }
          ],
          a: "completed"
        },
        
        {
          type: "mc",
          q: "What number comes after 11?",
          options: ["Twelve", "Twenty", "Two"],
          a: "Twelve",
          tip: "After 11 comes 12.",
          successTip: "Eleven and Twelve = special! NOT oneteen/twoteen ❌. After 12, pattern starts: thirteen, fourteen... 🔢"
        },
        
        {
          type: "order",
          q: "Traduce: 'Uno, dos, tres.'",
          words: ["One,", "two,", "three."],
          a: "One, two, three.",
          extra: ["four", "five"]
        },
        
        {
          type: "mc",
          q: "Carlos counts: 'One, two, three, ___'",
          options: ["four", "for", "forty"],
          a: "four",
          tip: "4 = four.",
          successTip: "Four vs For! FOUR = 4️⃣. FOR = para. Same pronunciation, different meaning! 🗣️"
        },
        
        {
          type: "mc",
          q: "What number is this? 10",
          options: ["Ten", "Eleven", "Twelve"],
          a: "Ten",
          tip: "10 = Ten."
        },
        
        {
          type: "mc",
          q: "How many months in a year?",
          options: ["Twelve", "Ten", "Eleven"],
          a: "Twelve",
          successTip: "12 months, 12 hours on a clock! Twelve is everywhere! 📅🕐"
        }
      ]
    },

    // ===================================================================
    // SET 4: TEEN VS TY (A1.0)
    // ===================================================================
    {
      id: "m1-u3-set4",
      title: "Set 4: 13 or 30?",
      context: `*Supermercado*

Carlos ve un precio: "£13"
Otro precio dice: "£30"
"ThirTEEN, ThirTY... ¡diferentes!" piensa.

*Supermarket*

Carlos sees a price: "£13"
Another price says: "£30"
"ThirTEEN, ThirTY... different!" he thinks.`,
      
      questions: [
        {
          type: "match",
          q: "Match the number to its word:",
          pairs: [
            { left: "13", right: "Thirteen" },
            { left: "30", right: "Thirty" },
            { left: "14", right: "Fourteen" }
          ],
          a: "completed",
          successTip: "TEEN vs TY! ThirTEEN (stress on TEEN) = 13. ThirTY (stress on THIR) = 30. Listen carefully! 👂"
        },
        
        {
          type: "mc",
          q: "Price: '£15'. That's ___.",
          options: ["Fifteen", "Fifty", "Five"],
          a: "Fifteen",
          tip: "15 = Fifteen (not 50)."
        },
        
        {
          type: "mc",
          q: "Price: '£50'. That's ___.",
          options: ["Fifty", "Fifteen", "Five"],
          a: "Fifty",
          tip: "50 = Fifty (not 15)."
        },
        
        {
          type: "match",
          q: "Match more numbers:",
          pairs: [
            { left: "16", right: "Sixteen" },
            { left: "60", right: "Sixty" },
            { left: "17", right: "Seventeen" }
          ],
          a: "completed"
        },
        
        {
          type: "mc",
          q: "What number is this? 18",
          options: ["Eighteen", "Eighty", "Eight"],
          a: "Eighteen",
          tip: "18 = Eighteen."
        },
        
        {
          type: "mc",
          q: "What number is this? 80",
          options: ["Eighty", "Eighteen", "Eight"],
          a: "Eighty",
          tip: "80 = Eighty."
        },
        
        {
          type: "match",
          q: "Match the last teen numbers:",
          pairs: [
            { left: "19", right: "Nineteen" },
            { left: "90", right: "Ninety" },
            { left: "20", right: "Twenty" }
          ],
          a: "completed",
          successTip: "After 19 comes 20! Then 21, 22, 23... Pattern changes! 📈"
        },
        
        {
          type: "order",
          q: "Traduce: 'Trece no es treinta.'",
          words: ["Thirteen", "is", "not", "thirty."],
          a: "Thirteen is not thirty.",
          extra: ["three", "tree", "the"]
        },
        
        {
          type: "mc",
          q: "What number comes after 19?",
          options: ["Twenty", "Thirty", "Nineteen"],
          a: "Twenty",
          tip: "After 19 = 20."
        },
        
        {
          type: "mc",
          q: "Carlos is 19. Next year he is ___.",
          options: ["twenty", "thirty", "ten"],
          a: "twenty",
          successTip: "Spelling! FORTY (not fourty ❌). Loses the 'u' from 'four'! Common mistake! ✍️"
        }
      ]
    },

    // ===================================================================
    // SET 5: SAYING YOUR AGE (A1+)
    // ===================================================================
    {
      id: "m1-u3-set5",
      title: "Set 5: How old are you?",
      context: `*Presentaciones en clase*

Profesora: "How old are you?"
Carlos: "I am 22 years old."
María: "I am 20."

*Class introductions*

Teacher: "How old are you?"
Carlos: "I am 22 years old."
María: "I am 20."`,
      
      questions: [
        {
          type: "match",
          q: "Match the question to its answer:",
          pairs: [
            { left: "How old are you?", right: "I am 20" },
            { left: "How old is he?", right: "He is 25" },
            { left: "How old are they?", right: "They are 30" }
          ],
          a: "completed",
          successTip: "'How old' questions use TO BE! Never 'have' in English for age! ⚠️"
        },
        
        {
          type: "mc",
          q: "I ___ 22 years old.",
          options: ["am", "have", "has"],
          a: "am",
          tip: "I am (not 'I have' for age).",
          successTip: "Spanish: Tengo 22 años (HAVE). English: I AM 22 years old (BE). Different verb! 🔄"
        },
        
        {
          type: "mc",
          q: "She ___ 25.",
          options: ["is", "am", "has"],
          a: "is",
          tip: "She is (not 'has')."
        },
        
        {
          type: "mc",
          q: "They ___ 30 years old.",
          options: ["are", "is", "have"],
          a: "are",
          tip: "They are (plural)."
        },
        
        {
          type: "order",
          q: "Traduce: 'Tengo veinte años.'",
          words: ["I", "am", "twenty", "years", "old."],
          a: "I am twenty years old.",
          extra: ["have", "has", "age"]
        },
        
        {
          type: "mc",
          q: "We ___ 18.",
          options: ["are", "is", "have"],
          a: "are",
          tip: "We are (plural)."
        },
        
        {
          type: "order",
          q: "Traduce: '¿Cuántos años tienes?'",
          words: ["How", "old", "are", "you?"],
          a: "How old are you?",
          extra: ["age", "years", "many"],
          successTip: "'How old...?' = THE age question! Standard phrase! 🎯"
        },
        
        {
          type: "mc",
          q: "Question: 'Are you 21?' Answer: '___'",
          options: ["Yes, I am", "Yes, I have", "Yes, it is"],
          a: "Yes, I am",
          tip: "Short answer with 'am'."
        },
        
        {
          type: "order",
          q: "Traduce: 'Él tiene treinta años.'",
          words: ["He", "is", "thirty", "years", "old."],
          a: "He is thirty years old.",
          extra: ["has", "have", "age"]
        },
        
        {
          type: "mc",
          q: "Question: 'Is she 16?' Answer: 'Yes, she ___.''",
          options: ["is", "are", "am"],
          a: "is",
          successTip: "You can drop 'years old'! 'I'm 22' = casual. 'I'm 22 years old' = complete. Both work! 💬"
        }
      ]
    },

    // ===================================================================
    // SET 6: NUMBERS 20-100 (A1+)
    // ===================================================================
    {
      id: "m1-u3-set6",
      title: "Set 6: Big Numbers",
      context: `*Leyendo números*

Carlos ve: "21, 35, 67, 100"
"Twenty-one, thirty-five, sixty-seven, one hundred!"
Practica en voz alta.

*Reading numbers*

Carlos sees: "21, 35, 67, 100"
"Twenty-one, thirty-five, sixty-seven, one hundred!"
He practices aloud.`,
      
      questions: [
        {
          type: "match",
          q: "Match the number to its word:",
          pairs: [
            { left: "21", right: "Twenty-one" },
            { left: "35", right: "Thirty-five" },
            { left: "67", right: "Sixty-seven" }
          ],
          a: "completed",
          successTip: "Compound numbers (21-99) use hyphens! Twenty-one, thirty-two, ninety-nine. Always! ✍️"
        },
        
        {
          type: "mc",
          q: "What number is this? 42",
          options: ["Forty-two", "Fourteen-two", "Four-two"],
          a: "Forty-two",
          tip: "42 = Forty-two."
        },
        
        {
          type: "mc",
          q: "What number is this? 55",
          options: ["Fifty-five", "Fifteen-five", "Five-five"],
          a: "Fifty-five",
          tip: "55 = Fifty-five."
        },
        
        {
          type: "match",
          q: "Match more numbers:",
          pairs: [
            { left: "88", right: "Eighty-eight" },
            { left: "99", right: "Ninety-nine" },
            { left: "100", right: "One hundred" }
          ],
          a: "completed",
          successTip: "100 = One hundred OR A hundred (both correct!). Casual vs formal! 💯"
        },
        
        {
          type: "mc",
          q: "Carlos is ___. (22 as word)",
          options: ["twenty-two", "twenty two", "twentytwo"],
          a: "twenty-two",
          tip: "Use hyphen: twenty-two."
        },
        
        {
          type: "order",
          q: "Traduce: 'Mi abuela tiene ochenta años.'",
          words: ["My", "grandmother", "is", "eighty."],
          a: "My grandmother is eighty.",
          extra: ["has", "years", "old"]
        },
        
        {
          type: "mc",
          q: "What number is this? 77",
          options: ["Seventy-seven", "Seventeen-seven", "Seven-seven"],
          a: "Seventy-seven",
          tip: "77 = Seventy-seven."
        },
        
        {
          type: "mc",
          q: "What number is this? 33",
          options: ["Thirty-three", "Thirteen-three", "Three-three"],
          a: "Thirty-three",
          tip: "33 = Thirty-three."
        },
        
        {
          type: "mc",
          q: "Bus number: '44'. That's ___.",
          options: ["Forty-four", "Fourteen-four", "Four-four"],
          a: "Forty-four",
          tip: "44 = Forty-four."
        },
        
        {
          type: "mc",
          q: "Room number: '99'. That's ___.",
          options: ["Ninety-nine", "Nineteen-nine", "Nine-nine"],
          a: "Ninety-nine",
          successTip: "Double numbers! Eleven (11), twenty-two (22), thirty-three (33)... Pattern! 🔄"
        }
      ]
    },

    // ===================================================================
    // SET 7: COUNTRIES (A1+)
    // ===================================================================
    {
      id: "m1-u3-set7",
      title: "Set 7: Where are you from?",
      context: `*Formulario de registro*

Carlos llena un formulario:
Name: Carlos
Age: 22
Country: Mexico

*Registration form*

Carlos fills out a form:
Name: Carlos
Age: 22
Country: Mexico`,
      
      questions: [
        {
          type: "match",
          q: "Match the country name:",
          pairs: [
            { left: "México", right: "Mexico" },
            { left: "España", right: "Spain" },
            { left: "Francia", right: "France" }
          ],
          a: "completed"
        },
        
        {
          type: "mc",
          q: "I am from ___. (USA)",
          options: ["the United States", "United States", "America"],
          a: "the United States",
          tip: "USA = the United States.",
          successTip: "USA, UK, UAE = use 'the'! The United States, The United Kingdom, The United Arab Emirates. 🌍"
        },
        
        {
          type: "match",
          q: "Match more countries:",
          pairs: [
            { left: "Alemania", right: "Germany" },
            { left: "Italia", right: "Italy" },
            { left: "China", right: "China" }
          ],
          a: "completed"
        },
        
        {
          type: "mc",
          q: "She is from ___. (Reino Unido)",
          options: ["the UK", "UK", "England"],
          a: "the UK",
          tip: "Reino Unido = the UK."
        },
        
        {
          type: "order",
          q: "Traduce: 'Soy de México.'",
          words: ["I", "am", "from", "Mexico."],
          a: "I am from Mexico.",
          extra: ["in", "at", "of"]
        },
        
        {
          type: "match",
          q: "Match the question to its answer:",
          pairs: [
            { left: "Where are you from?", right: "I am from Spain" },
            { left: "Where is she from?", right: "She is from Italy" },
            { left: "Where are they from?", right: "They are from Japan" }
          ],
          a: "completed",
          successTip: "'Where... from?' = country question! Always use FROM! 🗺️"
        },
        
        {
          type: "mc",
          q: "He is from ___. (Japón)",
          options: ["Japan", "Japanese", "Japón"],
          a: "Japan",
          tip: "Country name = Japan."
        },
        
        {
          type: "mc",
          q: "We are from ___. (Canadá)",
          options: ["Canada", "Canadian", "Canadá"],
          a: "Canada",
          tip: "Country name = Canada."
        },
        
        {
          type: "order",
          q: "Traduce: '¿De dónde eres?'",
          words: ["Where", "are", "you", "from?"],
          a: "Where are you from?",
          extra: ["is", "of", "from"]
        },
        
        {
          type: "mc",
          q: "They are from ___. (Brasil)",
          options: ["Brazil", "Brazilian", "Brasil"],
          a: "Brazil",
          successTip: "Country vs Nationality! Brazil (country) vs Brazilian (nationality). Next set! 🚀"
        }
      ]
    },

    // ===================================================================
    // SET 8: NATIONALITIES (A2-)
    // ===================================================================
    {
      id: "m1-u3-set8",
      title: "Set 8: What nationality?",
      context: `*Formulario - Nacionalidad*

Carlos escribe:
Country: Mexico
Nationality: Mexican

*Form - Nationality*

Carlos writes:
Country: Mexico
Nationality: Mexican`,
      
      questions: [
        {
          type: "match",
          q: "Match country to nationality:",
          pairs: [
            { left: "Mexico", right: "Mexican" },
            { left: "Spain", right: "Spanish" },
            { left: "France", right: "French" }
          ],
          a: "completed",
          successTip: "Country = NOUN (Mexico). Nationality = ADJECTIVE (Mexican). Different forms! 📝"
        },
        
        {
          type: "mc",
          q: "I am from Mexico. I am ___.",
          options: ["Mexican", "Mexico", "Mexicans"],
          a: "Mexican",
          tip: "Nationality = Mexican."
        },
        
        {
          type: "mc",
          q: "She is from Spain. She is ___.",
          options: ["Spanish", "Spain", "Spains"],
          a: "Spanish",
          tip: "Nationality = Spanish."
        },
        
        {
          type: "match",
          q: "Match more nationalities:",
          pairs: [
            { left: "USA", right: "American" },
            { left: "UK", right: "British" },
            { left: "Germany", right: "German" }
          ],
          a: "completed"
        },
        
        {
          type: "mc",
          q: "He is from Italy. He is ___.",
          options: ["Italian", "Italy", "Italians"],
          a: "Italian",
          tip: "Nationality = Italian."
        },
        
        {
          type: "mc",
          q: "We are from Japan. We are ___.",
          options: ["Japanese", "Japan", "Japaneses"],
          a: "Japanese",
          tip: "Nationality = Japanese.",
          successTip: "Japanese = same for singular AND plural! No 's'! One Japanese, two Japanese. ✅"
        },
        
        {
          type: "order",
          q: "Traduce: 'Soy mexicano.'",
          words: ["I", "am", "Mexican."],
          a: "I am Mexican.",
          extra: ["from", "Mexico", "in"]
        },
        
        {
          type: "mc",
          q: "They are from Canada. They are ___.",
          options: ["Canadian", "Canada", "Canadians"],
          a: "Canadian",
          tip: "Nationality adjective = Canadian.",
          successTip: "Adjectives don't have plurals! 'They are Canadian' ✅ (not Canadians ❌ in this position). 📚"
        },
        
        {
          type: "mc",
          q: "Question: 'What nationality are you?' Answer: 'I am ___.''",
          options: ["Brazilian", "Brazil", "from Brazil"],
          a: "Brazilian",
          tip: "Nationality question = adjective answer."
        },
        
        {
          type: "order",
          q: "Traduce: 'Ella es francesa.'",
          words: ["She", "is", "French."],
          a: "She is French.",
          extra: ["from", "France", "in"],
          successTip: "Two ways! 'I am FROM Mexico' OR 'I am Mexican'. Both correct! 🌎"
        }
      ]
    },

    // ===================================================================
    // SET 9: FULL INTRODUCTION (A2-)
    // ===================================================================
    {
      id: "m1-u3-set9",
      title: "Set 9: Introducing Yourself",
      context: `*Presentación completa*

Carlos se presenta:
"I am Carlos. I am 22 years old.
I am from Mexico. I am Mexican."

*Complete introduction*

Carlos introduces himself:
"I am Carlos. I am 22 years old.
I am from Mexico. I am Mexican."`,
      
      questions: [
        {
          type: "order",
          q: "Traduce: 'Me llamo Carlos.'",
          words: ["I", "am", "Carlos."],
          a: "I am Carlos.",
          extra: ["My", "name", "is"]
        },
        
        {
          type: "order",
          q: "Traduce: 'Tengo 22 años.'",
          words: ["I", "am", "22", "years", "old."],
          a: "I am 22 years old.",
          extra: ["have", "has", "age"]
        },
        
        {
          type: "order",
          q: "Traduce: 'Soy de México.'",
          words: ["I", "am", "from", "Mexico."],
          a: "I am from Mexico.",
          extra: ["in", "at", "of"]
        },
        
        {
          type: "order",
          q: "Traduce: 'Soy mexicano.'",
          words: ["I", "am", "Mexican."],
          a: "I am Mexican.",
          extra: ["from", "Mexico", "in"]
        },
        
        {
          type: "mc",
          q: "María says: 'I ___ María. I ___ 20. I ___ Spanish.'",
          options: ["am / am / am", "am / is / am", "is / am / am"],
          a: "am / am / am",
          tip: "I always uses 'am'.",
          successTip: "'I am' works for EVERYTHING! Name, age, nationality, job, feelings... Master 'I am' = master introductions! 🎯"
        },
        
        {
          type: "mc",
          q: "He says: 'My name ___ Pedro. I ___ from Spain.'",
          options: ["is / am", "am / am", "is / is"],
          a: "is / am",
          tip: "Name is / I am."
        },
        
        {
          type: "order",
          q: "Traduce: 'Ella se llama Ana y tiene 25 años.'",
          words: ["Her", "name", "is", "Ana", "and", "she", "is", "25."],
          a: "Her name is Ana and she is 25.",
          extra: ["has", "have", "years"]
        },
        
        {
          type: "mc",
          q: "Complete the introduction: 'I am Luis. I am ___ Mexico.'",
          options: ["from", "in", "at"],
          a: "from",
          tip: "Origin = FROM."
        },
        
        {
          type: "mc",
          q: "Complete: 'She is 30 ___ old. She is Italian.'",
          options: ["years", "year", "age"],
          a: "years",
          tip: "30 years old (plural)."
        },
        
        {
          type: "order",
          q: "Traduce: 'Somos de España y tenemos 28 años.'",
          words: ["We", "are", "from", "Spain", "and", "we", "are", "28."],
          a: "We are from Spain and we are 28.",
          extra: ["have", "has", "years"],
          successTip: "🎉 Perfect introduction structure! Name + Age + Origin + Nationality = complete! You got it! 🚀"
        }
      ]
    },

    // ===================================================================
    // SET 10: REVIEW & PRACTICE (A2-)
    // ===================================================================
    {
      id: "m1-u3-set10",
      title: "Set 10: Final Practice",
      context: `*Última clase*

Profesora: "Now, introduce yourself in English!"
Carlos: "I am Carlos. I am 22. I am from Mexico."
"Perfect!" dice la profesora.

*Last class*

Teacher: "Now, introduce yourself in English!"
Carlos: "I am Carlos. I am 22. I am from Mexico."
"Perfect!" says the teacher.`,
      
      questions: [
        {
          type: "mc",
          q: "Question: 'How old are you?' Answer: 'I ___ 25.'",
          options: ["am", "have", "has"],
          a: "am",
          tip: "I am (for age)."
        },
        
        {
          type: "mc",
          q: "Question: 'Where are you from?' Answer: 'I am ___ Brazil.'",
          options: ["from", "in", "at"],
          a: "from",
          tip: "Origin = FROM."
        },
        
        {
          type: "mc",
          q: "Question: 'What nationality are you?' Answer: 'I am ___.'",
          options: ["Brazilian", "Brazil", "from Brazil"],
          a: "Brazilian",
          tip: "Nationality = adjective."
        },
        
        {
          type: "order",
          q: "Traduce: '¿Cuántos años tiene ella?'",
          words: ["How", "old", "is", "she?"],
          a: "How old is she?",
          extra: ["are", "have", "years"]
        },
        
        {
          type: "mc",
          q: "She ___ 30. He ___ 35. They ___ married.",
          options: ["is / is / are", "is / are / are", "are / is / are"],
          a: "is / is / are",
          tip: "She is / He is / They are."
        },
        
        {
          type: "mc",
          q: "What number is this? 44",
          options: ["Forty-four", "Fourteen", "Four-four"],
          a: "Forty-four",
          tip: "44 = Forty-four."
        },
        
        {
          type: "mc",
          q: "What number is this? 77",
          options: ["Seventy-seven", "Seventeen", "Seven-seven"],
          a: "Seventy-seven",
          tip: "77 = Seventy-seven."
        },
        
        {
          type: "order",
          q: "Traduce: 'Tenemos treinta años.'",
          words: ["We", "are", "thirty", "years", "old."],
          a: "We are thirty years old.",
          extra: ["have", "has", "age"]
        },
        
        {
          type: "mc",
          q: "Complete: 'I am from France. I am ___.'",
          options: ["French", "France", "from French"],
          a: "French",
          tip: "Nationality = French."
        },
        
        {
          type: "order",
          q: "Traduce: 'Me llamo Carlos, tengo 22 años y soy mexicano.'",
          words: ["I", "am", "Carlos,", "I", "am", "22,", "and", "I", "am", "Mexican."],
          a: "I am Carlos, I am 22, and I am Mexican.",
          extra: ["have", "from", "years"],
          successTip: "🏆 UNIT 3 COMPLETE! You can introduce yourself in English! Name, age, nationality = MASTERED! Next: Unit 4! 🚀"
        }
      ]
    }
  ],

  readingSet: {
    id: "m1-u3-reading",
    title: "Reading: Student Profiles",
    category: "reading",
    
    questions: [
      {
        type: "passage",
        passage: `**Carlos - Student Profile**

My name is Carlos. I am 22 years old. I am from Mexico. I am Mexican. I am a student.`
      },
      
      {
        type: "mc",
        q: "How old is Carlos?",
        options: ["22", "20", "25"],
        a: "22",
        tip: "He says 'I am 22'."
      },
      
      {
        type: "mc",
        q: "Where is Carlos from?",
        options: ["Mexico", "Spain", "USA"],
        a: "Mexico",
        tip: "He is from Mexico."
      },
      
      {
        type: "mc",
        q: "What is Carlos's nationality?",
        options: ["Mexican", "Spanish", "American"],
        a: "Mexican",
        tip: "He is Mexican."
      },
      
      {
        type: "passage",
        passage: `**María - Student Profile**

Hi! I am María. I am 20 years old. I am from Spain. I am Spanish. My friend is Ana. She is 25. She is from Italy.`
      },
      
      {
        type: "mc",
        q: "How old is María?",
        options: ["20", "25", "22"],
        a: "20",
        tip: "María is 20."
      },
      
      {
        type: "mc",
        q: "Where is Ana from?",
        options: ["Italy", "Spain", "France"],
        a: "Italy",
        tip: "Ana is from Italy."
      },
      
      {
        type: "mc",
        q: "How old is Ana?",
        options: ["25", "20", "30"],
        a: "25",
        tip: "She is 25."
      },
      
      {
        type: "passage",
        passage: `**Pedro and Luis - Brothers**

We are brothers. I am Pedro. I am 30. My brother is Luis. He is 28. We are from Brazil. We are Brazilian.`
      },
      
      {
        type: "mc",
        q: "How old is Pedro?",
        options: ["30", "28", "32"],
        a: "30",
        tip: "Pedro is 30."
      },
      
      {
        type: "mc",
        q: "Where are they from?",
        options: ["Brazil", "Mexico", "Spain"],
        a: "Brazil",
        tip: "They are from Brazil."
      },
      
      {
        type: "mc",
        q: "What is their nationality?",
        options: ["Brazilian", "Mexican", "Spanish"],
        a: "Brazilian",
        successTip: "Perfect reading! You can understand personal information in English! 📖✅"
      }
    ]
  },

  reviewSet: {
    id: "m1-u3-review",
    title: "Unit 3 Review",
    category: "review",
    
    questions: [
      {
        type: "match",
        q: "Review: Match pronouns to 'to be':",
        pairs: [
          { left: "I", right: "am" },
          { left: "He/She/It", right: "is" },
          { left: "You/We/They", right: "are" }
        ],
        a: "completed"
      },
      
      {
        type: "mc",
        q: "Review: I ___ 25 years old.",
        options: ["am", "is", "have"],
        a: "am",
        tip: "I am (for age)."
      },
      
      {
        type: "mc",
        q: "Review: What number is 15?",
        options: ["Fifteen", "Fifty", "Five"],
        a: "Fifteen",
        tip: "15 = Fifteen."
      },
      
      {
        type: "mc",
        q: "Review: What number is 50?",
        options: ["Fifty", "Fifteen", "Five"],
        a: "Fifty",
        tip: "50 = Fifty."
      },
      
      {
        type: "order",
        q: "Review: Traduce: '¿Cuántos años tienes?'",
        words: ["How", "old", "are", "you?"],
        a: "How old are you?",
        extra: ["many", "years", "age"]
      },
      
      {
        type: "mc",
        q: "Review: I am from Mexico. I am ___.",
        options: ["Mexican", "Mexico", "from Mexican"],
        a: "Mexican",
        tip: "Nationality = Mexican."
      },
      
      {
        type: "mc",
        q: "Review: She ___ from Spain.",
        options: ["is", "am", "are"],
        a: "is",
        tip: "She is."
      },
      
      {
        type: "mc",
        q: "Review: They ___ 30 years old.",
        options: ["are", "is", "am"],
        a: "are",
        tip: "They are."
      },
      
      {
        type: "order",
        q: "Review: Traduce: 'Soy de Italia y tengo 28 años.'",
        words: ["I", "am", "from", "Italy", "and", "I", "am", "28."],
        a: "I am from Italy and I am 28.",
        extra: ["have", "has", "years"]
      },
      
      {
        type: "mc",
        q: "Review: What number is 77?",
        options: ["Seventy-seven", "Seventeen", "Seven"],
        a: "Seventy-seven",
        successTip: "🎉 UNIT 3 MASTERED! To be ✅ Numbers ✅ Age ✅ Nationality ✅ Ready for Unit 4: Family! 🚀"
      }
    ]
  }
};
